import { OptJobStatus, OptJobType, OptJobWithQueueItems, TERMINAL_OPT_JOB_STATUSES } from 'api/optJobs/optJobs.api'
import { QueueItem } from 'api/queueItems/queueItems.types'
import i18n from 'i18next'
import { ReactNode } from 'react'
import { NavigateFunction } from 'react-router'
import { countFinished } from 'ui/components/OptJobPhaseProgress/OptJobMultiPhaseProgress/OptJobMultiPhaseProgress.helper'
import { deriveQueueItemPhaseDurations, STEPS } from 'ui/components/OptJobPhaseProgress/OptJobPhaseProgress.helper'
import { toOptJobOutcome } from 'ui/optJobOutcome/optJobOutcome.helper'
import { OptJobOutcome } from 'ui/optJobOutcome/optJobOutcome.types'
import { getSubtypeDisplayName } from 'utils/systemUtils'
import { optJobTypeTranslationMapping } from 'views/OptimizationsView/Optimizations.helper'
import { onLinkClicked } from '../NotificationCenter.helper'

/**
 * Who triggered the job. `name` is null for a user whose first/last name is not
 * set — the backend sends `""` for them, which still means a person, not the
 * scheduler.
 */
export type OptJobCreator = { kind: 'automatic' } | { kind: 'user'; name: string | null }

/**
 * What the row counts, in the unit it counts in: a job with several queue items is
 * described by its scenarios, a job with one (or none created yet) by the phases that
 * item has been through. The row never shows both, so only the applicable one is
 * derived — for a multi-scenario job that skips a per-item derivation per tick whose
 * result nothing renders.
 */
export type OptJobLogRowProgress =
  | { kind: 'scenarios'; finished: number; total: number }
  | { kind: 'steps'; done: number; total: number }

export type OptJobLogRowViewModel =
  | { kind: 'running'; elapsedMs: number; creator: OptJobCreator; progress: OptJobLogRowProgress }
  | {
      kind: 'finished'
      finishedAt: ISODateTime
      elapsedMs: number
      creator: OptJobCreator
      progress: OptJobLogRowProgress
    }

export function deriveOptJobCreator({
  created_by,
  creator_name,
}: Pick<OptJobWithQueueItems, 'created_by' | 'creator_name'>): OptJobCreator {
  if (created_by == null && !creator_name) return { kind: 'automatic' }
  return { kind: 'user', name: creator_name?.trim() ? creator_name : null }
}

/**
 * The moment a terminal job reached its end state. Terminal statuses are sticky,
 * so `status_updated_at` closes the job's whole life cycle for failed and
 * successful jobs alike. `updated_at` is only a fallback for rows predating that
 * field; it also moves on writes that are not status transitions.
 */
export function getOptJobLifecycleEnd(optJobWithQueueItems: OptJobWithQueueItems): ISODateTime {
  return optJobWithQueueItems.status_updated_at ?? optJobWithQueueItems.updated_at
}

/**
 * When the row's clock starts. A published plan is the exception: the job is created the
 * moment publishing is requested, but the work it times only begins once the draft's
 * results are in, so everything before the first `result_received_at` belongs to the
 * draft optimization rather than to publishing it. Only queue items carry that stamp,
 * so the job's start is the earliest one across them — and a publish that has not
 * received any result yet has nothing better to measure from than its own creation.
 */
export function getOptJobLifecycleStart(
  optJobWithQueueItems: OptJobWithQueueItems,
  isPublishAction: boolean
): string | null {
  if (!isPublishAction) return optJobWithQueueItems.created_at

  let earliest: string | null = null
  let earliestMs = Number.POSITIVE_INFINITY

  for (const { result_received_at: resultReceivedAt } of optJobWithQueueItems.queue_items) {
    if (resultReceivedAt == null) continue

    const resultReceivedMs = new Date(resultReceivedAt).getTime()
    if (Number.isFinite(resultReceivedMs) && resultReceivedMs < earliestMs) {
      earliest = resultReceivedAt
      earliestMs = resultReceivedMs
    }
  }

  return earliest
}

/**
 * `queue_items` is empty until the backend has created the first row for the job
 * (e.g. a brand-new job still in `Created`), which still has a phase count to
 * show — one item's worth, none of it done.
 *
 * Step counts reuse the same timestamp-based derivation as the expanded panel so
 * the two can never disagree; `status` alone can lag behind the phase timestamps.
 */
function deriveProgress(queueItems: QueueItem[], now: number, jobHasEnded: boolean): OptJobLogRowProgress {
  if (queueItems.length > 1) {
    const { finished, total } = countFinished(queueItems)
    return { kind: 'scenarios', finished, total }
  }

  const [queueItem] = queueItems
  if (queueItem == null) return { kind: 'steps', done: 0, total: STEPS.length }

  const { steps } = deriveQueueItemPhaseDurations(queueItem, now, jobHasEnded)
  return { kind: 'steps', done: steps.filter((step) => step.state === 'done').length, total: steps.length }
}

export function computeElapsedMs(
  optJobWithQueueItems: OptJobWithQueueItems,
  terminal: boolean,
  now: number,
  startedAt: string | null = optJobWithQueueItems.created_at
): number {
  if (startedAt == null) return 0

  const startedMs = new Date(startedAt).getTime()
  if (!Number.isFinite(startedMs)) return 0
  const endMs = terminal ? new Date(getOptJobLifecycleEnd(optJobWithQueueItems)).getTime() : now
  if (!Number.isFinite(endMs)) return 0
  return Math.max(0, endMs - startedMs)
}

export function deriveOptJobLogRowViewModel(
  optJobWithQueueItems: OptJobWithQueueItems,
  now: number,
  { isPublishAction = false }: { isPublishAction?: boolean } = {}
): OptJobLogRowViewModel {
  const terminal = TERMINAL_OPT_JOB_STATUSES.has(optJobWithQueueItems.status)
  const startedAt = getOptJobLifecycleStart(optJobWithQueueItems, isPublishAction)
  const elapsedMs = computeElapsedMs(optJobWithQueueItems, terminal, now, startedAt)
  const creator = deriveOptJobCreator(optJobWithQueueItems)
  const progress = deriveProgress(optJobWithQueueItems.queue_items, now, terminal)

  if (!terminal) return { kind: 'running', elapsedMs, creator, progress }

  return {
    kind: 'finished',
    finishedAt: getOptJobLifecycleEnd(optJobWithQueueItems),
    elapsedMs,
    creator,
    progress,
  }
}

export function getOptJobTypeName(optJobType: OptJobType): string {
  switch (optJobType) {
    case 'regular':
      return i18n.t('A new production plan optimization')
    case 'regular_wip':
      return i18n.t('A new draft production plan optimization')
    case 'followup_with_deviations':
      return i18n.t('A follow-up (incl. production deviations) optimization')
    case 'followup_without_deviations':
      return i18n.t('A follow-up (optimal production) optimization')
    case 'followup_regular':
      return i18n.t('A follow-up optimization')
    case 'measvalues_calculations':
      return i18n.t('A measurement values calculation optimization')
    case 'marginal_cost_calculation':
      return i18n.t('A marginal cost calculation optimization')
    case 'sandbox':
      return i18n.t('A sandbox optimization')
    case 'real_time':
      return i18n.t('A real time optimization')
    case 'real_time_measvalues_calculations':
      return i18n.t('A real time measurement values calculation optimization')
    case 'fuel_plan':
      return i18n.t('A fuel plan optimization')
    case 'electricity_price_scenario':
      return i18n.t('An electricity price scenario optimization')
    case 'intraday_plan':
      return i18n.t('An intraday plan optimization')
    case 'settlement':
      return i18n.t('A settlements optimization')
    default:
      return i18n.t('A {{optJobType}} optimization', {
        optJobType: optJobTypeTranslationMapping(optJobType)?.toLowerCase() ?? optJobType,
      })
  }
}

/**
 * Under `use_draftproductionplan`, a `regular` job is not an optimization anyone asked
 * for — it is the step that publishes a finished draft. It gets its own wording and
 * icon, and has no solver phases worth breaking down, because there is no solve behind
 * it. Without the flag `regular` is the plan optimization itself and nothing changes.
 */
export function isPublishedPlanJob(optJobType: OptJobType, treatRegularAsPublished = false): boolean {
  return treatRegularAsPublished && optJobType === 'regular'
}

/** Publishing reads as its own sentence rather than a job type name with a suffix. */
function mapPublishedPlanToText(outcome: OptJobOutcome, subtypeDisplayName?: string): string {
  switch (outcome) {
    case 'created':
      return subtypeDisplayName
        ? i18n.t('A production plan for {{subtypeDisplayName}} is being published.', { subtypeDisplayName })
        : i18n.t('A production plan is being published.')
    case 'failed':
    case 'unsolvable':
      return subtypeDisplayName
        ? i18n.t('A production plan for {{subtypeDisplayName}} failed to publish.', { subtypeDisplayName })
        : i18n.t('A production plan failed to publish.')
    case 'finished':
      return subtypeDisplayName
        ? i18n.t('A production plan for {{subtypeDisplayName}} has been published.', { subtypeDisplayName })
        : i18n.t('A production plan has been published.')
  }
}

/** Every other job reads as its type name, then what became of it. */
function mapOptimizationToText(prefixText: string, outcome: OptJobOutcome, subtypeDisplayName?: string): string {
  switch (outcome) {
    case 'created':
      return subtypeDisplayName
        ? `${prefixText} ${i18n.t('for {{subtypeDisplayName}} is running.', { subtypeDisplayName })}`
        : `${prefixText} ${i18n.t('is running.')}`
    case 'failed':
      return subtypeDisplayName
        ? `${prefixText} ${i18n.t('for {{subtypeDisplayName}} has failed.', { subtypeDisplayName })}`
        : `${prefixText} ${i18n.t('has failed.')}`
    case 'unsolvable':
      return subtypeDisplayName
        ? `${prefixText} ${i18n.t('for {{subtypeDisplayName}} is unsolvable.', { subtypeDisplayName })}`
        : `${prefixText} ${i18n.t('is unsolvable.')}`
    case 'finished':
      return subtypeDisplayName
        ? `${prefixText} ${i18n.t('for {{subtypeDisplayName}} has finished.', { subtypeDisplayName })}`
        : `${prefixText} ${i18n.t('has finished.')}`
  }
}

/** Several subtypes are named inline, in the order the backend listed them. */
const SUBTYPE_SEPARATOR = ', '

/**
 * The subtypes the title names. A row with an expandable panel leaves several of them to
 * that panel, which gives each its own labelled matrix row, and names one in the title
 * only when that is all the job ran. A published plan has no panel — there is no solve
 * behind it — so its title carries every subtype it published, or nothing will.
 */
export function deriveTitleSubtypeName({
  subtypes,
  availableSubtypes,
  namesEverySubtype,
}: {
  subtypes: (string | null)[]
  availableSubtypes: Subtype[]
  namesEverySubtype: boolean
}): string | undefined {
  if (!namesEverySubtype && subtypes.length !== 1) return undefined

  const names = subtypes
    .map((subtype) => getSubtypeDisplayName(subtype, availableSubtypes))
    .filter((name) => name != null)

  return names.length > 0 ? names.join(SUBTYPE_SEPARATOR) : undefined
}

type OptJobLogRowText = {
  optJobType: OptJobType
  status: OptJobStatus
  /** What the job covers, as `deriveTitleSubtypeName` decided the title should say it. */
  subtypeDisplayName?: string
  treatRegularAsPublished?: boolean
}

/** The job status determines the title outcome; publishing treats Unsolvable as a failed publish. */
export function mapOptJobTypeNameAndStatusToText({
  optJobType,
  status,
  subtypeDisplayName,
  treatRegularAsPublished,
}: OptJobLogRowText): string {
  const isPublishedPlan = isPublishedPlanJob(optJobType, treatRegularAsPublished)
  const outcome = toOptJobOutcome(status, isPublishedPlan)

  return isPublishedPlan
    ? mapPublishedPlanToText(outcome, subtypeDisplayName)
    : mapOptimizationToText(getOptJobTypeName(optJobType), outcome, subtypeDisplayName)
}

export function getLinkToOptJobTypeModule(
  optJobType: OptJobType,
  navigate: NavigateFunction,
  period: { startTime: ISODateTime; endTime: ISODateTime } | null = null
): ReactNode {
  switch (optJobType) {
    case 'regular':
      return (
        <span className="underline cursor-pointer italic">
          <button
            type="button"
            className="bg-transparent border-none p-0 cursor-pointer underline italic"
            onClick={() => onLinkClicked('/optimize', navigate)}
          >
            {i18n.t('Go to the production plan')}
          </button>
          <i className="ml-1 fal fa-arrow-right" />
        </span>
      )
    case 'regular_wip':
      return (
        <span className="underline cursor-pointer italic">
          <button
            type="button"
            className="bg-transparent border-none p-0 cursor-pointer underline italic"
            onClick={() => onLinkClicked('/draftproductionplan', navigate)}
          >
            {i18n.t('Go to the draft production plan')}
            <i className="ml-1 fal fa-arrow-right" />
          </button>
        </span>
      )
    case 'followup_with_deviations':
    case 'followup_without_deviations':
    case 'followup_regular': {
      const startTimeDate = period?.startTime.split('T')[0]
      const endTimeDate = period?.endTime.split('T')[0]
      const link = `/followupproduction?startTime=${startTimeDate}&endTime=${endTimeDate}`

      return (
        <span className="underline cursor-pointer italic">
          <button
            type="button"
            className="bg-transparent border-none p-0 cursor-pointer underline italic"
            onClick={() => onLinkClicked(period ? link : '/followupproduction', navigate)}
          >
            {i18n.t('Go to the follow-up module')}
          </button>
          <i className="ml-1 fal fa-arrow-right" />
        </span>
      )
    }

    case 'fuel_plan': {
      return (
        <span className="underline cursor-pointer italic">
          <button
            type="button"
            className="bg-transparent border-none p-0 cursor-pointer underline italic"
            onClick={() => onLinkClicked('/fuelplan', navigate)}
          >
            {i18n.t('Go to the fuel plan module')}
          </button>
          <i className="ml-1 fal fa-arrow-right" />
        </span>
      )
    }

    case 'settlement':
      // No dedicated settlement module to link to yet; add a link once it exists.
      return null
    default: {
      return null
    }
  }
}
